import { Copy, ExternalLink } from 'lucide-react'
import { IconButton } from '@/components/ui/Button'
import { ReviewActions } from '@/components/review/ReviewActions'
import { FavoriteToggle } from '@/components/review/FavoriteToggle'
import { TagEditor } from '@/components/review/TagEditor'
import { PreviouslyRejectedFlag } from '@/components/review/VerdictHistory'
import { MediaThumb } from './MediaThumb'
import { mediaMeta } from './meta'
import { pathOf } from '@/lib/format'
import type { MediaItem } from '@/types'

/** Star, tag, and approve/reject all act inline; only the thumbnail opens the drawer. */
export function MediaCard({
  item,
  tagPool,
  onOpen,
  onFavorite,
  onTags,
  onReview,
  onCopyUrl,
}: {
  item: MediaItem
  tagPool: string[]
  onOpen: () => void
  onFavorite: (next: boolean) => void
  onTags: (tags: string[]) => void
  onReview: (status: 'approved' | 'rejected') => void
  onCopyUrl: () => void
}) {
  const label = item.description ?? item.title
  return (
    <li className="flex flex-col gap-2.5 rounded-[6px] border border-rule bg-surface p-3">
      <button
        type="button"
        onClick={onOpen}
        className="text-left"
        aria-label={`Open ${label ?? pathOf(item.url)}`}
      >
        <MediaThumb item={item} />
      </button>

      <div className="flex items-start gap-1">
        <div className="flex-1">
          {item.title && <p className="truncate font-head text-[13px] font-semibold">{item.title}</p>}
          <p className="line-clamp-2 text-[13px] leading-5">
            {item.description ?? <span className="text-ink-faint">No description</span>}
          </p>
        </div>
        <FavoriteToggle favorite={item.favorite} onChange={onFavorite} />
      </div>

      <div className="flex items-center gap-2 text-[11px] text-ink-faint">
        <span className="tabular shrink-0">{mediaMeta(item)}</span>
        {!item.usable && (
          <span className="shrink-0 rounded-[3px] bg-surface-sunk px-1.5 py-0.5 font-head font-semibold text-ink-muted">
            Not usable
          </span>
        )}
        {item.sourceUrl && (
          <span className="truncate font-mono" title={item.sourceUrl}>
            {pathOf(item.sourceUrl)}
          </span>
        )}
        <span className="ml-auto flex">
          <IconButton label="Copy media URL" className="h-6 w-6" onClick={onCopyUrl}>
            <Copy size={12} aria-hidden />
          </IconButton>
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            aria-label="Open original"
            className="inline-flex h-6 w-6 items-center justify-center rounded-[4px] text-ink-muted hover:bg-surface-sunk"
          >
            <ExternalLink size={12} aria-hidden />
          </a>
        </span>
      </div>

      <TagEditor tags={item.tags} suggestions={tagPool} onChange={onTags} />

      {item.previouslyRejected && <PreviouslyRejectedFlag compact />}

      <ReviewActions
        status={item.status}
        onApprove={() => onReview('approved')}
        onReject={() => onReview('rejected')}
        className="mt-auto"
      />
    </li>
  )
}
